import { compact } from 'lodash';
import React from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import type { PrintOrientationTypeEnum } from 'venn-api';
import { StatefulInput } from 'venn-components';
import { DEFAULT_LANDSCAPE_WIDTH, DEFAULT_PORTRAIT_WIDTH } from 'venn-state';
import type { ThemeProvider } from 'venn-ui-kit';
import { ColorUtils, GetColor, getHelpCenterLinks, GetTypography, Icon, Label, ZIndex } from 'venn-ui-kit';
import { TOP_TOOLBAR_HEIGHT } from './top-bar/shared';
import type { SetterOrUpdater } from 'recoil';
import { cn } from '@/lib/utils';

export const RedistributionWarnMessage = ({ showLink }: { showLink: boolean }) => (
  <span>
    This report contains data that requires an external use license. PDF exports will be limited to “Draft” mode.
    {showLink && (
      <NotificationLink to={{ pathname: getHelpCenterLinks().REDISTRIBUTION_HELP_HREF }} target="_blank">
        &nbsp;Learn More.
      </NotificationLink>
    )}
  </span>
);

export const RedistributionCorrectMessage = () => (
  <span>
    All data that requires an external use license has been removed. PDFs can be exported in either “Draft” of “Final”
    mode.
    <NotificationLink to={{ pathname: getHelpCenterLinks().REDISTRIBUTION_HELP_HREF }} target="_blank">
      &nbsp;Learn More.
    </NotificationLink>
  </span>
);

export const StyledLabel = styled(Label)`
  line-height: 1.3;
`;

export const PanelLabel = styled(Label)`
  line-height: 1.2;
  margin-bottom: 8px;
  display: inline-block;
`;

export const Flex = ({ children, className, ...props }: React.BaseHTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={cn('flex gap-x-[5px] items-center', className)}>
    {children}
  </div>
);

export const FlexHeader = ({ children, className, ...props }: React.BaseHTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={cn('flex gap-x-[5px] items-center font-bold text-[14px] my-2.5 mx-0', className)}>
    {children}
  </div>
);

export const MAIN_LEFT_PANEL_WIDTH = 300;
const LEFT_BLOCK_CONFIG_PANEL_WIDTH = 311;
const COLLAPSED_PANEL_WIDTH = 30;
export const ASK_VENN_PANEL_WIDTH = 380;

export const PageContent = styled.div<{
  isMainLeftPanelOpen: boolean;
  isAllocatorOpen: boolean;
  allocatorPanelWidth: number;
  isLeftBlockConfigPanelOpen: boolean;
  isAskVennOpen?: boolean;
  inPrintMode?: boolean;
  orientation?: PrintOrientationTypeEnum;
}>`
  width: 100%;
  height: calc(100vh - 50px);
  display: grid;
  grid-template-rows: max-content 1fr;
  grid-template-columns: 1fr;
  grid-template-areas:
    'topbar'
    'content';

  ${({ isMainLeftPanelOpen, isAllocatorOpen, isLeftBlockConfigPanelOpen, isAskVennOpen }) => {
    const mainLeftPanelWidth = `${isMainLeftPanelOpen ? MAIN_LEFT_PANEL_WIDTH : COLLAPSED_PANEL_WIDTH}px`;
    // Use 'auto' for allocator width to allow resizing
    const allocatorWidth = isAllocatorOpen ? 'auto' : '';
    const blockConfigPanelWidth = isLeftBlockConfigPanelOpen ? `${LEFT_BLOCK_CONFIG_PANEL_WIDTH}px` : '';

    const topbarCount =
      /* main left panel */
      1 +
      Number(isAllocatorOpen) +
      Number(isLeftBlockConfigPanelOpen) +
      /* content */
      1 +
      Number(!!isAskVennOpen);
    const topbarAreas = Array.from({ length: topbarCount }, () => 'topbar').join(' ');
    const templateAreas = compact([
      isAllocatorOpen && 'allocator',
      'leftsidebar',
      isLeftBlockConfigPanelOpen && 'blockConfigPanel',
      'content',
      isAskVennOpen && 'askVenn',
    ]).join(' ');

    const gridColumns = compact([
      isAllocatorOpen ? allocatorWidth : null,
      mainLeftPanelWidth,
      isLeftBlockConfigPanelOpen ? blockConfigPanelWidth : null,
      '1fr',
      isAskVennOpen ? `${ASK_VENN_PANEL_WIDTH}px` : null,
    ]).join(' ');

    return css`
      grid-template-columns: ${gridColumns};
      grid-template-areas: '${topbarAreas}' '${templateAreas}';
    `;
  }}

  ${(props) =>
    props.inPrintMode &&
    css`
      && {
        height: auto;
        display: block;
      }
    `} ${(props) =>
    props.orientation !== 'PORTRAIT' &&
    props.inPrintMode &&
    css`
      && {
        @media screen {
          width: ${DEFAULT_LANDSCAPE_WIDTH}px;
        }
      }
    `} ${(props) =>
    props.orientation === 'PORTRAIT' &&
    props.inPrintMode &&
    css`
      && {
        @media screen {
          width: ${DEFAULT_PORTRAIT_WIDTH}px;
        }
      }
    `} @media print {
    display: block;
    width: 100%;
    margin: 0px;
    padding: 0px;
  }
`;

export const TopControl = styled.div`
  grid-area: topbar;
  min-height: ${TOP_TOOLBAR_HEIGHT};
`;

export const ContentStyle = css`
  background-color: ${ColorUtils.hex2rgbaFrom(GetColor.GreyScale.Grey10, 0.5)};
  font-family: ${GetTypography.fontFamily};
  grid-area: content;

  button,
  input,
  select,
  textarea {
    font-family: inherit;
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
  }

  @media print {
    background-color: transparent;
  }
`;
export const Content = styled.div`
  ${ContentStyle}
`;

export const ContentCover = styled(Content)`
  background-color: ${ColorUtils.hex2rgbaFrom(GetColor.White, 0.85)};
  height: 100%;

  display: flex;
  align-items: center;
  padding: 0 200px;
  z-index: ${ZIndex.StickyCover};

  > div {
    text-align: center;
  }
`;

export const LeftPanel = styled.div<{ unSeenPanel?: boolean; studioV1?: boolean }>`
  grid-area: leftsidebar;
  height: calc(100vh - ${TOP_TOOLBAR_HEIGHT} - 50px);
`;

export const Panel = styled.div<{ unSeenPanel?: boolean }>`
  grid-area: sidebar;
  height: calc(100vh - ${TOP_TOOLBAR_HEIGHT} - 50px);
  z-index: ${(props) => (props.unSeenPanel ? ZIndex.Background : ZIndex.Base)};
  position: relative;
`;

export const Section = styled.div<{ noBorder?: boolean; disabled?: boolean; refreshedStyling?: boolean }>`
  padding: ${({ refreshedStyling }) => (refreshedStyling ? '0' : '10px 10px 20px')};
  ${({ noBorder }) =>
    !noBorder &&
    css`
      border-bottom: 1px solid ${GetColor.GreyScale.Grey30};
    `};

  ${({ disabled }) => disabled && 'opacity: 0.5;'}
`;

export const BorderBottom = styled.div`
  padding: 0 15px 0 20px;
  border-bottom: 1px solid ${GetColor.GreyScale.Grey30};
`;

export const GapWrapper = styled.div`
  margin-top: 20px;
`;

export const DelimitedWrapper = styled.div`
  label {
    justify-content: flex-start;
  }

  border-top: 1px solid ${GetColor.GreyScale.Grey30};
  margin: 20px -20px 0 -20px;
  padding: 16px 20px 0 20px;
`;

export const Text = styled.div`
  font-size: 12px;
  color: ${GetColor.GreyScale.Grey70};
`;

export const TextInline = styled(Text)`
  display: inline-block;
`;

export const MAX_WIDTH = 1;
export const MIN_WIDTH = 0.5;
export const UNIT_WIDTH = 0.5;

export const CUSTOM_FOOTER_MAX_LENGTH = 150;

export const InputField = styled(StatefulInput)`
  display: flex;

  & > span {
    font-size: 12px;
    left: 0;
  }

  ${({ disabled }) =>
    disabled &&
    css`
      background-color: ${GetColor.GreyScale.Grey40};
    `}
`;

export const ImgWrapper = styled.div`
  display: inline-block;
  height: 20px;
  overflow: hidden;
`;

export const Img = styled.img`
  height: 100%;
  max-width: auto;
`;

export const Message = styled.div<{ colorOverride?: ThemeProvider }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 10px 15px;
  font-size: 10px;
  border-color: ${GetColor.GreyScale.Grey30};
  margin-bottom: 10px;
  background: ${(props) =>
    props.colorOverride
      ? props.colorOverride(props)
      : props.colorOverride || ColorUtils.opacifyFrom(GetColor.HighlightLight, 0.1)};
  column-gap: 10px;
  margin-top: 3px;

  i {
    color: ${GetColor.HighlightDark};
    font-size: 14px;
  }

  button {
    color: ${GetColor.HighlightDark};
    min-height: auto;
    min-width: auto;
    padding: 0;
  }
`;

export const UploadImageText = styled.div<{ disabled?: boolean; customFontSize?: string; customColor?: string }>`
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};

  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-content: flex-start;
  align-items: center;

  height: 24px;
  font-weight: bold;
  font-size: ${({ customFontSize }) => customFontSize ?? '14px'};
  line-height: 24px;
  color: ${({ disabled, customColor }) =>
    disabled ? GetColor.GreyScale.Grey90 : (customColor ?? GetColor.Primary.Dark)};
`;
export const PlusIcon = styled.div<{ marginless?: boolean }>`
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-content: center;
  align-items: center;

  width: 24px;
  height: 24px;
  font-weight: bold;
  font-size: 14px;
  margin-right: ${({ marginless }) => (marginless ? '0' : '5px')};

  border: ${({ marginless }) => (marginless ? '0' : '1px')} solid ${GetColor.Primary.Dark};
  box-sizing: border-box;
  border-radius: 2px;
`;

const NotificationLink = styled(Link)`
  color: ${GetColor.White};
  font-weight: bold;
`;

export const Field = styled.div`
  font-weight: bold;
  display: flex;
  align-items: center;
`;

export const InfoIcon = styled(Icon)<{ customColor?: string }>`
  margin: 0 3px;
  color: ${({ customColor }) => customColor ?? GetColor.GreyScale.Grey80};
`;

export interface ImageProps {
  selected?: boolean;
  left?: boolean;
  disabled?: boolean;
}

export interface RCMCustomizationProps {
  isRcmCustomization?: boolean;
  rotated?: boolean;
}

export const getHideToggleConfig = ([isHidden, setter]: [boolean, SetterOrUpdater<boolean>]) => {
  const toggleValue = () => setter((oldValue) => !oldValue);
  return { checked: !isHidden, onChange: toggleValue };
};
